import React from "react";
import LogisticsCompany from "@/enum/LogisticsCompany";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import LogisticsCompanyNameMap from "@/map/LogisticsCompanyNameMap";
import { EwaybillBranchAccount, EwaybillShippingAddress } from "@/services/api/ewaybillAccount";
import { trimStringValueNormalize } from "@/util/formNormalizes";
import { getLimitByteLengthRule, getRequiredRule } from "@/util/formValidatorRules";
import { Button, Form, Input, InputNumber, message, Modal } from "antd";
import { useRequest } from "ahooks";
import { getShareCode } from "@/services/api/ewaybillAccountShare";
import copy from "copy-to-clipboard";
import { InfoCircleTwoTone } from "@ant-design/icons";
import { getFormLayout } from "@/util";
import { useModalStore } from "@/hooks/Modal/hooks";

const [formItemLayout] = getFormLayout(4);

interface ShareEwaybillModalProps extends InjectBaseModalProps {
  logisticsCompany: LogisticsCompany;

  ewaybillBranchAccount: EwaybillBranchAccount;

  shippingAddress: EwaybillShippingAddress;
}

interface FormValues {
  quantity: number;
  consumerName: string;
}

interface ShareEwaybillResultModalProps extends InjectBaseModalProps {
  shareCode: string;
}

/**
 * 结果弹框
 */
const ShareEwaybillResultModal: React.FC<ShareEwaybillResultModalProps> = function (props) {
  const { visible, onCancel, shareCode } = props;

  return (
    <Modal
      key="copyShareCodeModal"
      title="发货易授权码"
      visible={visible}
      onCancel={onCancel}
      width={550}
      destroyOnClose={true}
      cancelButtonProps={{
        hidden: true,
      }}
      okText="关闭"
      onOk={onCancel}
    >
      <div>
        <span className={"ml5 fs13"}>复制发货易授权码给要分享的人，对方在发货易中添加即可打单</span>
      </div>
      {shareCode != null ? (
        <div className={"mt20 fs13 noWrap"}>
          <div className={"ml5"}>
            <span className={"blackColor"}>发货易授权码：</span>
            <span className={"blackColor"}>{shareCode}</span>

            <Button
              className="ml15"
              type="primary"
              onClick={() => {
                copy(`${shareCode} 使用教程：https://www.yuque.com/docs/share/176f6bb8-5be6-48b4-960f-08a60cac3ae2?`);

                message.success("授权码复制成功，发送给你要分享的人吧～");
              }}
            >
              复制
            </Button>
          </div>
          <div className={"mt20"}>
            <InfoCircleTwoTone twoToneColor="#faad14" className="mr10" />
            <span className={"attentionColor"}>授权码两小时内有效，且只能用一次，若要分享给多人，请分别生成授权码</span>
          </div>
        </div>
      ) : null}
    </Modal>
  );
};

/**
 * 生成授权码弹框
 */
const ShareEwaybillModal: React.FC<ShareEwaybillModalProps> = function (props) {
  const { visible, onCancel, ewaybillBranchAccount, shippingAddress, logisticsCompany } = props;

  const modalStore = useModalStore();
  const [form] = Form.useForm<FormValues>();

  const { branchName, waybillNoTypeName, branchCode } = ewaybillBranchAccount;

  const newbranchName = branchName ? branchName : "";
  const newWaybillNoTypeName = waybillNoTypeName ? "-" + waybillNoTypeName : "";

  const branchAccountName = newbranchName + "(" + branchCode + ")" + newWaybillNoTypeName;
  const { province, city, district, town, street } = shippingAddress;
  const joinedShippingAddress = [province, city, district, town, street].filter((word) => word).join("");

  const { run, loading } = useRequest(
    async () => {
      const values = await form.validateFields();

      let result = await getShareCode(shippingAddress.id, values.quantity, values.consumerName);

      if (result?.code) {
        let shareCode = result.code;

        onCancel?.();

        modalStore.openModal(ShareEwaybillResultModal, {
          shareCode,
        });
      }
    },
    {
      manual: true,
    }
  );

  return (
    <Modal
      title="共享电子面单给他人"
      visible={visible}
      okText="生成授权码"
      cancelText="关闭"
      onOk={run}
      confirmLoading={loading}
      onCancel={onCancel}
      width={550}
      destroyOnClose={true}
    >
      <Form {...formItemLayout} form={form}>
        <Form.Item label="分享数量" name="quantity" rules={[getRequiredRule("请输入分享数量")]}>
          <InputNumber
            placeholder="请输入"
            style={{
              width: "100%",
            }}
            precision={0}
            min={0}
          />
        </Form.Item>

        <Form.Item
          label="备注"
          name="consumerName"
          normalize={trimStringValueNormalize}
          rules={[getRequiredRule("请输入备注"), getLimitByteLengthRule(100, "备注名长度不能超过50个字")]}
        >
          <Input placeholder="请输入对方备注，方便您查看是谁使用你的单号" />
        </Form.Item>

        <Form.Item label="物流公司：">
          <span>{LogisticsCompanyNameMap[logisticsCompany]}</span>
        </Form.Item>

        <Form.Item label="快递网点：">
          <span>{branchAccountName}</span>
        </Form.Item>

        <Form.Item label="发货地址">
          <span>{joinedShippingAddress}</span>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default React.memo(ShareEwaybillModal);
